import React, { ReactNode, useEffect } from 'react';
import { FormInstance, Select, Space } from 'antd';
import {
  InfoTooltip,
  InputNumber,
  Form,
  Col,
  Row,
  Input,
} from '@common/components';
export type SuppliersManagementTierFormValues = {
  name: string;
  fee: number;
  visibilityOffset: number;
};

type SuppliersManagementTierFormProps = {
  onFinish: (values: SuppliersManagementTierFormValues) => void;
  onFormInstanceReady: (
    instance: FormInstance<SuppliersManagementTierFormValues>
  ) => void;
  initialValues?: Partial<SuppliersManagementTierFormValues>;
  actions?: ReactNode;
};

const DefaultValues: Partial<SuppliersManagementTierFormValues> = {
  name: undefined,
  visibilityOffset: 0,
  fee: 0,
};

export function SuppliersManagementTierForm({
  onFormInstanceReady,
  onFinish,
  initialValues = DefaultValues,
  actions,
}: SuppliersManagementTierFormProps) {
  const [form] = Form.useForm<SuppliersManagementTierFormValues>();
  const initialTimeUnit =
    initialValues?.visibilityOffset && initialValues.visibilityOffset % 24 === 0
      ? 'days'
      : 'hours';
  const [timeUnit, setTimeUnit] = React.useState<'hours' | 'days'>(
    initialTimeUnit
  );

  useEffect(() => {
    onFormInstanceReady(form);
  }, [form, onFormInstanceReady]);

  const onFinishWrapper = (values: SuppliersManagementTierFormValues) => {
    if (timeUnit === 'days') {
      values.visibilityOffset *= 24;
    }

    onFinish(values);
  };

  const initialValuesAdjustedByTimeUnit = {
    ...initialValues,
    visibilityOffset:
      initialValues?.visibilityOffset && initialTimeUnit === 'days'
        ? initialValues.visibilityOffset / 24
        : initialValues.visibilityOffset,
  };

  const setFormFee = (value: number) => {
    const fee = Math.floor(value * 100) / 100;
    form.setFieldValue('fee', fee);
  };

  const validateMessages = {
    required: "Value can't be blank",
  };

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValuesAdjustedByTimeUnit}
        validateMessages={validateMessages}
        onFinish={onFinishWrapper}
        preserve={false}
        className="supplier-management-tier-form"
      >
        <Form.Item label="Name" required>
          <Row gutter={16}>
            <Col flex="auto">
              <Form.Item noStyle name="name" rules={[{ required: true }]}>
                <Input
                  placeholder="Tier Name"
                  style={{ width: '100%' }}
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col>{actions}</Col>
          </Row>
        </Form.Item>
        <Form.Item
          label={
            <>
              Staffing Request Delay
              <InfoTooltip
                title="A zeroed (0) value means there is no delay."
                leftOffset="lg"
              />
            </>
          }
          required
        >
          <Space.Compact block size="large">
            <Form.Item
              noStyle
              name="visibilityOffset"
              rules={[{ required: true }]}
            >
              <InputNumber
                type="number"
                min={0}
                style={{ width: '100%' }}
                size="large"
              />
            </Form.Item>
            <Form.Item noStyle>
              <Select value={timeUnit} onChange={setTimeUnit} size="large">
                <Select.Option value="hours">Hour(s)</Select.Option>
                <Select.Option value="days">Day(s)</Select.Option>
              </Select>
            </Form.Item>
          </Space.Compact>
        </Form.Item>
        <Form.Item
          label="Admin Fee"
          name="fee"
          required
          rules={[{ required: true }]}
          style={{ marginBottom: 0 }}
        >
          <InputNumber
            min={0}
            max={100}
            onChange={(value: number | null) => value && setFormFee(value)}
            addonAfter="%"
            style={{ width: '50%' }}
            size="large"
          />
        </Form.Item>
      </Form>
    </>
  );
}
